import { PlusOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BeaconPositionEvent,
  FromToFilter,
  Position,
  usePositionHistory,
} from "../../../api";
import { useComputeFromToDateRange } from "../../../util/useComputeFromToDateRange";
import { BaseCard } from "../../BaseCard";
import { BeaconTelemetryFilterValue } from "../../BeaconTelemetryFilter";
import { Chart, SeriesDefinition } from "../../chart/Chart";
import { DownloadPositions } from "../../DownloadPositions";
import { BeaconLocationDrawer, SCBOneLocationDrawer } from "./LocationDrawer";

interface SCBOneLocationCardProps {
  positions: Position[] | undefined;
  operationRunSelected: boolean;
  isLoading: boolean;
}

export function SCBOneLocationCard({
  positions,
  operationRunSelected,
  isLoading,
}: SCBOneLocationCardProps) {
  const { t } = useTranslation();

  const [locationBatteryVisible, setLocationBatteryVisible] = useState(false);

  const handleDrawerOpenClose = (state: boolean) => {
    setLocationBatteryVisible(state);
  };

  const locationSeriesDefinition: SeriesDefinition[] = [
    {
      name: t("horizontal-accuracy"),
      dataSet: positions
        ? positions.map((p) => ({
            ts: new Date(p.ts).getTime(),
            value: p.acc.h,
          }))
        : [],
      color: "#439199",
    },
  ];

  return (
    <BaseCard title={t("beacon:position-data")}>
      <Chart
        seriesDefintions={locationSeriesDefinition}
        isLoading={isLoading}
        alertMessage={
          operationRunSelected
            ? t("scb-one:run-notification-no-data")
            : t("scb-one:run-notification-select")
        }
        height="80%"
      />
      <SCBOneLocationDrawer
        isLoading={isLoading}
        operationRunSelected={operationRunSelected}
        positionSeriesDefinition={locationSeriesDefinition}
        locationDrawerVisible={locationBatteryVisible}
        onCloseDrawer={handleDrawerOpenClose}
      />
      <Button
        className="show-card-drawer-button"
        type="primary"
        onClick={() => handleDrawerOpenClose(true)}
        icon={<PlusOutlined />}
      />
    </BaseCard>
  );
}

interface BeaconLocationCardProps {
  selectedFromToFilter:
    | (FromToFilter & number)
    | (FromToFilter & {
        from: Date;
        to: Date;
      });
  filter: BeaconTelemetryFilterValue;
  beaconSerial: string;
}

export function BeaconLocationCard({
  selectedFromToFilter,
  filter,
  beaconSerial,
}: BeaconLocationCardProps) {
  const [locationDrawerVisible, setLocationDrawerVisible] = useState(false);

  const { t } = useTranslation();

  const positionHistory = usePositionHistory(
    beaconSerial,
    selectedFromToFilter
  );

  const filteredPositionHistory = useMemo(() => {
    if (filter === "GpsOnly") {
      return positionHistory.value?.items?.filter(
        (p) => p.position?.cprs === 0
      );
    } else if (filter === "AccurateCorrectedPosition") {
      return positionHistory.value?.items?.filter(
        (p) => p.position?.cprs === 2
      );
    } else {
      return positionHistory.value?.items;
    }
  }, [positionHistory.value?.items, filter]);

  const hAccData = useMemo(() => {
    return (
      filteredPositionHistory?.map((item: BeaconPositionEvent) => ({
        ts: new Date(item.ts).getTime(),
        value: item.position?.acc?.h as number,
      })) || []
    );
  }, [positionHistory.value?.items, filteredPositionHistory]);

  const hDopData = useMemo(() => {
    return (
      filteredPositionHistory?.map((item: BeaconPositionEvent) => ({
        ts: new Date(item.ts).getTime(),
        value: item.position?.dop?.h as number,
      })) || []
    );
  }, [positionHistory.value?.items, filteredPositionHistory]);

  const environmentSeriesDefintions: SeriesDefinition[] = useMemo(() => {
    return [
      {
        name: t("horizontal-accuracy"),
        dataSet: hAccData,
        color: "#0A03EB",
      },
      {
        name: t("hdop"),
        dataSet: hDopData,
        color: "#F3B25D",
      },
    ];
  }, [hAccData, hDopData]);

  const positionFromToDateRange = useComputeFromToDateRange(
    positionHistory.lastLoadedAt,
    selectedFromToFilter
  );

  return (
    <BaseCard title={t("beacon:position-data")}>
      <Chart
        seriesDefintions={environmentSeriesDefintions}
        isLoading={positionHistory.loading}
        height="80%"
      />
      {!!filteredPositionHistory?.length && (
        <DownloadPositions
          beaconId={beaconSerial}
          positions={filteredPositionHistory || []}
          style={{ position: "absolute", bottom: 10, right: 40 }}
          from={positionFromToDateRange.from}
          to={positionFromToDateRange.to}
        />
      )}
      <BeaconLocationDrawer
        beaconId={beaconSerial}
        positions={filteredPositionHistory || []}
        positionHistoryIsLoading={positionHistory.loading}
        positionSeriesDefinition={environmentSeriesDefintions}
        positionFromToDateRange={positionFromToDateRange}
        locationDrawerVisible={locationDrawerVisible}
        onCloseDrawer={() => setLocationDrawerVisible(false)}
      />
      <Button
        className="show-card-drawer-button"
        type="primary"
        onClick={() => setLocationDrawerVisible(true)}
        icon={<PlusOutlined />}
      />
    </BaseCard>
  );
}
